import React, { useState, useEffect, useCallback } from 'react';
import SuperAdminLayout from '@/components/superadmin/SuperAdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CreditCard, Plus, Search } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';

const SuperAdminPayments = ({ auth, onLogout }) => {
  const [tenants, setTenants] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ tenant_id: '', amount: '', method: 'virement', reference: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [tenantsRes, logsRes] = await Promise.all([
        axios.get(`${API}/saas/tenants`),
        axios.get(`${API}/saas/logs?limit=200`),
      ]);
      setTenants(tenantsRes.data);
      // Extract payment logs and gather all payments from each tenant
      const allPayments = [];
      for (const t of tenantsRes.data) {
        try {
          const pRes = await axios.get(`${API}/saas/tenants/${t.id}/payments`);
          pRes.data.forEach(p => { p.company_name = t.company_name; allPayments.push(p); });
        } catch {}
      }
      allPayments.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
      setPayments(allPayments);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async () => {
    if (!form.tenant_id || !form.amount) { toast.error('Entreprise et montant requis'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/saas/payments`, { ...form, amount: parseFloat(form.amount) });
      toast.success('Paiement enregistré');
      setShowForm(false);
      setForm({ tenant_id: '', amount: '', method: 'virement', reference: '', notes: '' });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); } finally { setSaving(false); }
  };

  const formatDate = (iso) => { if (!iso) return '-'; return new Date(iso).toLocaleDateString('fr-CH'); };
  const formatCHF = (v) => `CHF ${(v || 0).toFixed(2)}`;

  const filtered = search ? payments.filter(p => (p.company_name || '').toLowerCase().includes(search.toLowerCase()) || (p.reference || '').toLowerCase().includes(search.toLowerCase())) : payments;
  const totalAmount = filtered.reduce((s, p) => s + (p.amount || 0), 0);

  return (
    <SuperAdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-6 lg:p-8 space-y-6" data-testid="superadmin-payments">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Paiements</h1>
            <p className="text-sm text-slate-400">Suivi des paiements reçus</p>
          </div>
          <Button onClick={() => setShowForm(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="add-payment-button">
            <Plus className="w-4 h-4 mr-2" /> Nouveau paiement
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <Card className="bg-slate-900 border-slate-800 p-5">
            <p className="text-xs text-slate-400 uppercase tracking-wider">Total encaissé</p>
            <p className="text-2xl font-bold text-emerald-400 mt-1">{formatCHF(totalAmount)}</p>
          </Card>
          <Card className="bg-slate-900 border-slate-800 p-5">
            <p className="text-xs text-slate-400 uppercase tracking-wider">Nombre de paiements</p>
            <p className="text-2xl font-bold text-white mt-1">{filtered.length}</p>
          </Card>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher par entreprise ou référence..." className="pl-10 bg-slate-900 border-slate-700 text-white placeholder:text-slate-500" />
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-slate-400 text-center py-20">Chargement...</div>
        ) : filtered.length === 0 ? (
          <Card className="bg-slate-900 border-slate-800 p-12 text-center">
            <CreditCard className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">Aucun paiement</p>
          </Card>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-800">
            <table className="w-full text-sm" data-testid="payments-table">
              <thead>
                <tr className="bg-slate-900/50 border-b border-slate-800">
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase tracking-wider">Date</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase tracking-wider">Entreprise</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase tracking-wider">Montant</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase tracking-wider hidden md:table-cell">Méthode</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase tracking-wider hidden lg:table-cell">Référence</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} className="border-b border-slate-800/50 hover:bg-slate-900/30">
                    <td className="py-3 px-4 text-slate-400 text-xs">{formatDate(p.date)}</td>
                    <td className="py-3 px-4 text-white">{p.company_name}</td>
                    <td className="py-3 px-4 text-emerald-400 font-medium">{formatCHF(p.amount)}</td>
                    <td className="py-3 px-4 text-slate-400 hidden md:table-cell capitalize">{p.method}</td>
                    <td className="py-3 px-4 text-slate-500 hidden lg:table-cell">{p.reference || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Payment Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Enregistrer un paiement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-slate-300 text-xs">Entreprise *</Label>
              <Select value={form.tenant_id} onValueChange={(v) => setForm({ ...form, tenant_id: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1" data-testid="payment-tenant-select">
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300 text-xs">Montant (CHF) *</Label>
              <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bg-slate-800 border-slate-700 text-white mt-1" data-testid="payment-amount" />
            </div>
            <div>
              <Label className="text-slate-300 text-xs">Méthode de paiement</Label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="virement">Virement bancaire</SelectItem>
                  <SelectItem value="carte">Carte de crédit</SelectItem>
                  <SelectItem value="leasing">Leasing</SelectItem>
                  <SelectItem value="especes">Espèces</SelectItem>
                  <SelectItem value="bvr">BVR / QR-facture</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300 text-xs">Référence</Label>
              <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="bg-slate-800 border-slate-700 text-white mt-1" placeholder="N° de transaction..." />
            </div>
            <div>
              <Label className="text-slate-300 text-xs">Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-slate-800 border-slate-700 text-white mt-1" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowForm(false)} className="border-slate-600 text-slate-300">Annuler</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="save-payment-button">
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </SuperAdminLayout>
  );
};

export default SuperAdminPayments;
